import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { MENUS, MENUS_ES } from "../../data/menus";
import { IMG } from "../../data/assets";
import { useLanguage } from "../../lib/i18n";

const MenuDetail = ({ active, lang }) => (
  <motion.div key={`${active.id}-${lang}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="bg-[--surface] border border-white/10 rounded-2xl p-8 md:p-12 h-full">
    <h3 className="font-display text-3xl md:text-4xl font-light text-[--text] mb-10">{active.name}</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-10">
      {active.sections.map((s) => (
        <div key={s.title}>
          <p className="font-mono uppercase tracking-[0.2em] text-[11px] text-[--gold] mb-4 pb-2 border-b border-white/10">{s.title}</p>
          <ul className="space-y-3">
            {s.items.map((item, i) => (
              <li key={i}>
                <p className="font-display text-base text-[--text] leading-snug">{item.t}</p>
                {item.d ? <p className="text-[--text-2] text-sm leading-relaxed mt-1">{item.d}</p> : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </motion.div>
);

export const MenusContent = () => {
  const [activeId, setActiveId] = useState("flavours-of-spain");
  const [detailOpen, setDetailOpen] = useState(false);
  const { t, lang } = useLanguage();
  const menus = lang === "es" ? MENUS_ES : MENUS;
  const active = menus.find((m) => m.id === activeId);
  const menucats = t("menucats");
  const navigate = useNavigate();

  useEffect(() => {
    if (!detailOpen) return;
    const onKey = (e) => e.key === "Escape" && setDetailOpen(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [detailOpen]);

  const selectMenu = (id) => {
    setActiveId(id);
    if (window.matchMedia("(max-width: 1023px)").matches) setDetailOpen(true);
  };

  const goQuote = () => {
    setDetailOpen(false);
    navigate("/request-a-quote");
  };

  return (
    <>
      <div className="max-w-3xl mb-10 mx-auto bg-[--surface] border border-white/10 rounded-2xl p-8 md:p-12 text-center">
        <h2 className="font-display font-light tracking-tight leading-[1.05] text-3xl sm:text-4xl md:text-5xl text-[--text]">
          {t("menus.h2a")} <span className="italic text-[--gold]">{t("menus.h2b")}</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-6">
          {menucats.map((cat) => (
            <div key={cat.group} className="bg-[--surface] border border-white/10 rounded-2xl p-6">
              <p className="font-mono uppercase tracking-[0.2em] text-[11px] text-[--gold] mb-4 pb-3 border-b border-white/10">{cat.group}</p>
              <div className="flex flex-wrap gap-2">
                {cat.items.map((it) => {
                  const isActive = it.id === activeId;
                  return (
                    <button key={it.id} data-testid={`menu-chip-${it.id}`} onClick={() => selectMenu(it.id)} className={`px-4 py-2 rounded-full border text-[11px] font-mono uppercase tracking-[0.1em] transition-colors duration-300 ${isActive ? "bg-[--gold] text-[#14180E] border-[--gold]" : "bg-transparent text-[--text-2] border-white/15 hover:border-[--gold] hover:text-[--text]"}`}>
                      {it.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="hidden lg:block lg:col-span-7">
          <AnimatePresence mode="wait">
            <div data-testid={`menu-detail-${active.id}`}>
              <MenuDetail active={active} lang={lang} />
            </div>
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-8 relative overflow-hidden rounded-2xl border border-[--olive]">
        <img src={IMG.tablescape} alt="Mediterranean tablescape" className="photo-grade absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative bg-[#14180E]/70 p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-xl">
            <h3 className="font-display text-2xl md:text-3xl text-[--text] mb-2">{t("menus.bespokeH")}</h3>
            <p className="text-[--text-2] text-sm leading-relaxed">{t("menus.bespokeP")}</p>
          </div>
          <button data-testid="bespoke-menu-btn" onClick={goQuote} className="shrink-0 inline-flex items-center justify-center px-8 py-4 rounded-full bg-[--gold] text-[#14180E] font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-[--text] transition-colors duration-400">
            {t("menus.bespokeBtn")}
          </button>
        </div>
      </div>

      {/* Mobile: selected menu opens as a pop-up */}
      <AnimatePresence>
        {detailOpen && (
          <motion.div
            data-testid="menu-detail-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-6"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div data-testid="menu-detail-backdrop" onClick={() => setDetailOpen(false)} className="absolute inset-0 bg-[#0B0E07]/85 backdrop-blur-md" />
            <motion.div
              initial={{ opacity: 0, y: 48 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 32 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              data-lenis-prevent
              className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[--bg] p-5 sm:p-8"
            >
              <button data-testid="menu-detail-close" onClick={() => setDetailOpen(false)} aria-label="Close menu" className="sticky top-0 float-right z-10 w-10 h-10 rounded-full border border-white/15 bg-[--surface] flex items-center justify-center text-[--text-2] hover:text-[--text] hover:border-[--gold] transition-colors">
                <X size={18} strokeWidth={1.5} />
              </button>
              <MenuDetail active={active} lang={lang} />
              <button data-testid="menu-detail-quote-btn" onClick={goQuote} className="mt-6 w-full inline-flex items-center justify-center px-8 py-4 rounded-full bg-[--gold] text-[#14180E] font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-[--text] transition-colors duration-400">
                {t("nav.quote")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
